import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  OnInit,
} from "@angular/core";
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { BehaviorSubject, Observer } from "rxjs";
import { ByStatus } from "../../../../model/filters/by-status";
import { SingleChoice } from "../../../../model/singlechoice";
import { StatusService } from "../../../../services/status.service";
import { NavigationHelper } from "../../../../services/navigation.helper";
import { FilterService } from "primeng/api";
import { Router } from "@angular/router";
import { Labeler } from "../../../../model/labeler";
import { Filter } from "../../../../model/filters/filter";
import { NotComponent } from "../not/not.component";
import { MatchAny } from "../../../../model/filters/match.any";
import { Navigation } from "../../../../model/navigation";
@Component({
  selector: "by-status-form",
  templateUrl: "./by-status-form.component.html",
  styleUrls: ["./by-status-form.component.css"],
})
export class ByStatusFormComponent implements OnInit {
  @Input() filter: ByStatus = new ByStatus();
  @Output() applyFilter = new EventEmitter<ByStatus>();
  @Output() choiceEvent: EventEmitter<SingleChoice> = new EventEmitter();

  byStatusForm!: FormGroup;

  statusLabeler = new BehaviorSubject<string>("-");
  completionUrl = new BehaviorSubject<string>("tickets/-1/filter/by.status");
  currentId: number | null = null;
  editingIndex: number | null = null;
  statusFilter: ByStatus = new ByStatus();
  @ViewChild(NotComponent) notComponent!: NotComponent;

  constructor(
    private fb: FormBuilder,
    private statusService: StatusService,
    private nav: NavigationHelper,
    private filterService: FilterService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const existingFilters =
      this.filter instanceof MatchAny ? this.filter.filters : [this.filter];

    const statusIdsFromFilter: number[] = existingFilters
      .filter((f): f is ByStatus => f instanceof ByStatus)
      .map((f) => f.id)
      .filter((id) => id !== undefined) as number[];

    const storedStatusIds = localStorage.getItem("statusIds");
    const statusIdsFromStorage = storedStatusIds
      ? (JSON.parse(storedStatusIds) as number[])
      : [];

    const mergedIds = Array.from(
      new Set([...statusIdsFromFilter, ...statusIdsFromStorage])
    );

    this.initializeForm(mergedIds);

    if (mergedIds.length > 0) {
      this.initializeForm(mergedIds);
    } else {
      this.byStatusForm = this.fb.group({
        statuses: this.fb.array([this.fb.control(null, Validators.required)]),
      });
    }

    if (mergedIds.length > 0) {
      this.currentId = mergedIds[0];
      this.statusLabeler.next(this.statusService.labelForId(this.currentId));
    }

    const storedEditingIndex = localStorage.getItem("editingStatusIndex");
    if (storedEditingIndex) {
      this.editingIndex = Number(storedEditingIndex);
    }
  }

  initializeForm(statusIds: number[]): void {
    const controls = statusIds.map((id) =>
      this.fb.control(id, Validators.required)
    );
    if (controls.length === 0)
      controls.push(this.fb.control(null, Validators.required));

    this.byStatusForm = this.fb.group({
      statuses: this.fb.array(controls),
    });
  }

  restoreStatuses(statusIds: (number | null)[]): void {
    this.byStatusForm.setControl("statuses", this.fb.array([]));
    for (const id of statusIds) {
      this.statuses.push(this.fb.control(id, Validators.required));
    }
  }

  apply(): void {
    const ids: number[] = this.statuses.value.filter(
      (id: number | null | undefined): id is number =>
        id !== null && id !== undefined
    );

    if (ids.length === 0) return;

    localStorage.setItem("statusIds", JSON.stringify(ids));

    let finalFilter: Filter;

    if (ids.length === 1) {
      finalFilter = new ByStatus(ids[0]);
    } else {
      finalFilter = new MatchAny(ids.map((id) => new ByStatus(id)));
    }

    this.notComponent.filter = finalFilter;
    const modifiedFilter: Filter = this.notComponent.getModifiedFilter();

    this.applyFilter.emit(modifiedFilter);
 
  }

  onFilterChanged(finalFilter: Filter) {
    this.applyFilter.emit(finalFilter);
    
  }

  relaySingleChoice(choice: SingleChoice) {
    this.nav.startChoice(choice);
  }

  onStatusChoice(statusId: number): void {
    if (this.editingIndex !== null && this.statuses.at(this.editingIndex)) {
      this.statuses.at(this.editingIndex).setValue(statusId);
    } else if (this.statuses.length > 0) {
      this.statuses.at(0).setValue(statusId);
    }

    const currentValues = this.statuses.value.filter((id) => id !== null);
    localStorage.setItem("statusIds", JSON.stringify(currentValues));

    this.currentId = statusId;
    this.statusLabeler.next(this.statusService.labelForId(statusId));

    this.router
      .navigate(["tickets/-1/filter/by.status/" + statusId])
      .then(() => {
        this.nav.freshNavigation;
      });
  }

  addStatusField(): void {
    this.statuses.push(this.fb.control(null, Validators.required));

    const currentValues = this.statuses.value.filter((id) => id !== null);
    localStorage.setItem("statusIds", JSON.stringify(currentValues));
  }

  removeField(index: number): void {
    if (this.statuses.length > 1) {
      this.statuses.removeAt(index);
      const currentValues = this.statuses.value.filter((id) => id !== null);
      localStorage.setItem("statusIds", JSON.stringify(currentValues));
    }
  }

  setEditingIndex(index: number): void {
    this.editingIndex = index;
    localStorage.setItem("editingStatusIndex", index.toString());
  }

  get statuses(): FormArray<FormControl> {
    return this.byStatusForm.get("statuses") as FormArray<FormControl>;
  }

  get statusObserver(): Partial<Observer<number>> {
    return {
      next: (id) => {
        this.onStatusChoice(id);
      },
    };
  }

  get fakeId(): number {
    return Navigation.NEW;
  }

  get statusLabelerByService(): Labeler {
    return this.statusService;
  }
}
